/**
 * The reservation's onboarding journey, as held by the DMS.
 *
 * When the back office sends the documents it chooses which after-arrival steps
 * this tenant has to do — the inventory (step 5) and the insurance certificate
 * (step 6) — and whether an annex travels with the lease. The DMS also records
 * how far the tenant has got, which is what lets someone close the tab after
 * signing and come back days later for the inventory.
 *
 * The tenant arrives holding the id from their emailed link (?id=…), which is
 * the key everything here is fetched by.
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "constant/constant";
import { Locale } from "../i18n/locale";
import { Reservation, RESERVATION } from "./reservation";
import { getChannelLogo } from "../channelLogos";
import { StepId } from "./onboardingSteps";

export type DocumentRole = "contract" | "annex";

export interface OnboardingDocument {
  title: string;
  role: DocumentRole;
  /**
   * How long this document is, measured while the package was built. Null for
   * anything sent before page counts were recorded.
   */
  pages?: number | null;
  /**
   * This document stored on its own, so "Preview" opens the lease or the annex
   * rather than the combined package under both buttons.
   *
   * Empty for a package sent before per-document files existed, and empty if
   * that one upload failed. Callers fall back to `document_url` -- the package
   * -- which is what every row opened before.
   */
  url?: string;
  /**
   * True for the attestation the system generates. It has no stored file: its
   * contents depend on a reason the tenant picks after the package was built,
   * so the review screen renders it on request instead.
   */
  generated?: boolean;
}

export type StepState = "pending" | "completed";

export interface OnboardingStepRecord {
  state: StepState;
  completed_at?: string | null;
}

/** The reservation exactly as the API sends it, before it is shaped for the UI. */
interface ApiReservation {
  tenantName: string;
  tenantFirstName: string;
  property: string;
  propertyBadge: string;
  address: string;
  district: string;
  bookingReference: string;
  channel: string;
  photo: string;
  checkIn: string | null;
  checkOut: string | null;
  inspectionPhotoCount: number;
  maxTenantPhotos: number;
  insuranceDueDays: number;
}

export interface Onboarding {
  id: string;
  generated_contract_pdf_id: string;
  flow: "A" | "B";
  /** The API speaks "english" / "french"; the UI speaks "en" / "fr". */
  language: "english" | "french" | "dutch";
  status: "pending" | "in_progress" | "completed";
  current_step: StepId;
  steps: Partial<Record<StepId, OnboardingStepRecord>>;
  inventory_step: boolean;
  insurance_step: boolean;
  contract_signed: boolean;
  /** The emailed PDF: lease plus annexes, one file, not yet countersigned. */
  document_url: string;
  documents: OnboardingDocument[];
  /** What the tenant declared for the generated attestation, if there is one. */
  stay_reason?: "professional" | "studies";
  identity: {
    file_name: string;
    uploaded_at: string | null;
  };
  inventory: {
    /** Photos taken at the property's last cleaning, for the tenant to review. */
    cleaning_photos: string[];
    comments: string;
    photo_urls: string[];
    signed_at: string | null;
    /** The assembled signed inventory report; "" until the step is signed. */
    pdf_url: string;
  };
  insurance: {
    file_url: string;
    file_name: string;
    uploaded_at: string | null;
  };
  reservation: ApiReservation;
}

export const localeForLanguage = (language: Onboarding["language"]): Locale =>
  language === "french" ? "fr" : "en";

/** True when an annex was sent alongside the lease — one signature covers both. */
export const hasAnnex = (onboarding: Onboarding): boolean =>
  onboarding.documents.some((document) => document.role === "annex");

// The screens want a plain calendar date; the API sends an ISO timestamp.
const toCalendarDate = (value: string | null): string =>
  value ? String(value).slice(0, 10) : "";

/**
 * Shape the API's reservation into what the screens read.
 *
 * Anything the DMS has no value for keeps the placeholder rather than rendering
 * blank — a nameless property with no dates reads as broken, and the tenant has
 * no way to tell a missing field from a broken page.
 */
export const toReservation = (api: ApiReservation): Reservation => ({
  channel: api.channel || RESERVATION.channel,
  channelLogo: getChannelLogo(api.channel) || RESERVATION.channelLogo,
  photo: api.photo || RESERVATION.photo,
  tenantName: api.tenantName || RESERVATION.tenantName,
  tenantFirstName: api.tenantFirstName || RESERVATION.tenantFirstName,
  property: api.property || RESERVATION.property,
  propertyBadge: api.propertyBadge || RESERVATION.propertyBadge,
  district: api.district || RESERVATION.district,
  address: api.address || RESERVATION.address,
  bookingReference: api.bookingReference || RESERVATION.bookingReference,
  checkIn: toCalendarDate(api.checkIn) || RESERVATION.checkIn,
  checkOut: toCalendarDate(api.checkOut) || RESERVATION.checkOut,
  inspectionPhotoCount: api.inspectionPhotoCount ?? RESERVATION.inspectionPhotoCount,
  maxTenantPhotos: api.maxTenantPhotos ?? RESERVATION.maxTenantPhotos,
  insuranceDueDays: api.insuranceDueDays ?? RESERVATION.insuranceDueDays,
});

export const fetchOnboarding = async (id: string): Promise<Onboarding | null> => {
  const response = await axios.get(
    `${base_url}/onboarding/get_onboarding?id=${encodeURIComponent(id)}`
  );
  if (response.data?.error !== false) return null;
  return response.data.onboarding as Onboarding;
};

/**
 * Tell the DMS the tenant finished a step.
 *
 * Deliberately not awaited by the navigation: a tenant who has done the work
 * should not be held on a screen because the write is slow, and the step is
 * recorded again if they revisit. Failures are logged, not surfaced.
 */
export const completeStep = async (id: string, step: StepId): Promise<void> => {
  try {
    await axios.post(`${base_url}/onboarding/complete_step`, { id, step });
  } catch (error) {
    console.error(`Failed to record onboarding step "${step}":`, error);
  }
};

/**
 * Submit the tenant's signature for the document set.
 *
 * The server rebuilds the package from the record it holds and stamps the
 * signature on it — the only thing it takes from here is the drawn image, so a
 * tampered request cannot change what was signed.
 *
 * Awaited, unlike step completion: a signature that silently failed to save
 * would leave the tenant believing they had signed a lease that nobody holds.
 */
export const submitContractSignature = async (
  generatedPdfId: string,
  signatureData: string,
  /**
   * The reason the tenant declared, when the package carries the generated
   * attestation. The API confines it to the two permitted values and ignores
   * it for any package without one.
   */
  stayReason?: string
): Promise<boolean> => {
  try {
    const response = await axios.post(`${base_url}/contract/save-signature`, {
      signatureData,
      generatedPdfId,
      stayReason,
    });
    return response.data?.error === false;
  } catch (error) {
    console.error("Failed to submit contract signature:", error);
    return false;
  }
};

/**
 * Save the countersigned documents to the tenant's device.
 *
 * Routed through the API rather than linking at the S3 URL directly: the API
 * streams the file back with an attachment disposition, so this is a real
 * download rather than a tab that opens the PDF, and it does not depend on the
 * bucket allowing cross-origin reads from this app.
 *
 * Only meaningful once the contract is signed — before that the stored file is
 * the unsigned copy that was emailed.
 */
export const downloadSignedDocuments = async (
  generatedPdfId: string,
  title: string
): Promise<boolean> => {
  // Built on the same fetch and save the final screen uses, rather than its own
  // copy of both. The duplicate carried two faults this one does not: it revoked
  // the object URL on the line after click(), and it wrapped whatever came back
  // as a PDF — so an error the API returned with a 200 was saved as a document
  // and reported as a success.
  const blob = await fetchSignedContractBlob(generatedPdfId, title);
  if (!blob) return false;
  saveBlob(blob, `${title}.pdf`);
  return true;
};

/** Trigger a browser "Save" for an already-fetched blob. */
/**
 * How long an object URL is kept alive after its download has been triggered.
 *
 * click() only *queues* the download; the browser reads the blob afterwards.
 * Revoking on the very next line — which is what this used to do — can pull the
 * data away before that read happens, and the file then never arrives, with no
 * error anywhere. Generous rather than tight: the cost of holding a few PDFs a
 * little longer is some memory, and the cost of being too quick is a document
 * the tenant believes they downloaded.
 */
const OBJECT_URL_TTL_MS = 60_000;

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
};

/**
 * Whether what came back is really a document.
 *
 * Both download endpoints answer 200 with a JSON body when they cannot produce
 * the file, and `responseType: "blob"` turns that into a Blob rather than into
 * a thrown error — so the failure looks exactly like success. Re-labelling it
 * application/pdf, which is what happened before, hands the tenant a .pdf full
 * of an error message and hides the problem completely.
 */
const asPdfBlob = async (data: Blob, label: string): Promise<Blob | null> => {
  if (data?.type && data.type.includes("json")) {
    const text = await data.text().catch(() => "");
    console.error(`${label}: the API returned an error, not a document —`, text.slice(0, 200));
    return null;
  }
  return new Blob([data], { type: "application/pdf" });
};

/** Fetch the signed contract package as a blob (no save). */
const fetchSignedContractBlob = async (
  generatedPdfId: string,
  title: string
): Promise<Blob | null> => {
  try {
    const response = await axios.post(
      `${base_url}/contract/download-signed-contract`,
      { id: generatedPdfId, title },
      { responseType: "blob" }
    );
    return await asPdfBlob(response.data, "Signed contract");
  } catch (error) {
    console.error("Failed to fetch the signed contract:", error);
    return null;
  }
};

/** Fetch the signed inventory report as a blob (no save). */
const fetchInventoryBlob = async (
  generatedPdfId: string
): Promise<Blob | null> => {
  try {
    const response = await axios.get(
      `${base_url}/onboarding/download_inventory?generated_contract_pdf_id=${encodeURIComponent(generatedPdfId)}`,
      { responseType: "blob" }
    );
    return await asPdfBlob(response.data, "Inventory report");
  } catch (error) {
    console.error("Failed to fetch the inventory document:", error);
    return null;
  }
};

/** What actually reached the tenant's device. */
export interface DownloadResult {
  /** The signed package: the lease and every annex it was sent with. */
  contract: boolean;
  /** The signed inventory report, when the journey produced one. */
  inventory: boolean;
  /** True when the journey has an inventory report to hand over at all. */
  inventoryExpected: boolean;
}

/**
 * Save every document the tenant is owed — the signed contract package, which
 * carries the lease and any annexes, and the signed inventory report.
 *
 * Both files are fetched first (in parallel), then saved back-to-back in the
 * same tick. Triggering the second save only after the first had already
 * finished its own network round trip put it far enough past the click that the
 * browser dropped it as a stray download — so the inventory never arrived. That
 * constraint is why the saves are not spaced out here.
 *
 * What comes back says which files were actually produced, so a caller can tell
 * the tenant that something is missing. Reporting nothing is how "it downloads
 * only the inventory report" went unnoticed.
 */
export const downloadTenantDocuments = async (
  generatedPdfId: string,
  contractTitle: string,
  includeInventory: boolean
): Promise<DownloadResult> => {
  const [contractBlob, inventoryBlob] = await Promise.all([
    fetchSignedContractBlob(generatedPdfId, contractTitle),
    includeInventory ? fetchInventoryBlob(generatedPdfId) : Promise.resolve(null),
  ]);

  if (contractBlob) saveBlob(contractBlob, `${contractTitle}.pdf`);
  if (inventoryBlob) saveBlob(inventoryBlob, "Inventory report.pdf");

  return {
    contract: !!contractBlob,
    inventory: !!inventoryBlob,
    inventoryExpected: includeInventory,
  };
};

/** A File as a base64 data URL, which is how this API takes uploads. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const MAX_PHOTO_DIMENSION = 1600;
/** JPEG quality for tenant photos — the same range photo-sharing apps default to. */
const PHOTO_JPEG_QUALITY = 0.78;

/**
 * A tenant-picked inventory photo as a base64 JPEG, resized and recompressed
 * first.
 *
 * A phone photo comes in at whatever resolution the camera shoots — often
 * several thousand pixels wide and, added up across a full batch, tens of
 * megabytes. None of that detail is needed to record an apartment's
 * condition, and the size is what made the inventory PDF's render (built
 * server-side from these photos) prone to timing out on a full batch.
 * Downscaled to a sane viewing size and re-encoded at a normal JPEG quality —
 * the same level photo-sharing apps use by default — a photo comes out
 * visually equivalent at a fraction of the size.
 *
 * Assumes an image file — a PDF can't be drawn onto a canvas. Identity and
 * insurance uploads route through `toDocumentBase64` below, which only calls
 * this for the image case and sends PDFs through `toBase64` untouched.
 */
const toCompressedPhotoBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_PHOTO_DIMENSION / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          // Canvas unsupported for some reason — send the original rather
          // than losing the photo entirely.
          resolve(String(reader.result));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", PHOTO_JPEG_QUALITY));
      };
      // Not a decodable image (an odd format the browser can't open) — send
      // the original rather than losing the photo entirely.
      img.onerror = () => resolve(String(reader.result));
      img.src = String(reader.result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * A document upload (identity, insurance) as base64.
 *
 * An image is downscaled and recompressed the same way inventory photos are —
 * these are phone-camera scans of ID cards or certificates, and the same
 * oversized-upload problem applies. A PDF is sent as-is: it can't go through
 * the canvas resize, and it's an official document where re-encoding isn't
 * wanted anyway.
 */
const toDocumentBase64 = (file: File): Promise<string> =>
  file.type === "application/pdf" ? toBase64(file) : toCompressedPhotoBase64(file);

/**
 * Submit the tenant's inventory: their comments, their photos and the signature
 * confirming they reviewed the property.
 *
 * Awaited, like the contract signature: a tenant who believes they have filed
 * an inventory and has not is exactly the situation the step exists to prevent.
 */
export interface SubmitInventoryResult {
  photo_urls: string[];
}

/**
 * `error` carries the server's own message (e.g. "You can add up to 10
 * photos.") so the screen can show why the save failed instead of a generic
 * "check your connection" — that generic copy is reserved for the `catch`
 * below, where there is no server response to read a message from at all.
 */
export const submitInventory = async (
  id: string,
  comments: string,
  photos: File[],
  signature: string
): Promise<{ ok: true; data: SubmitInventoryResult } | { ok: false; error: string | null }> => {
  try {
    const encoded = await Promise.all(photos.map(toCompressedPhotoBase64));
    const response = await axios.post(`${base_url}/onboarding/submit_inventory`, {
      id,
      comments,
      photos: encoded,
      signature,
    });
    if (response.data?.error !== false) {
      return { ok: false, error: response.data?.title || null };
    }
    return { ok: true, data: response.data.inventory };
  } catch (error) {
    console.error("Failed to submit the inventory:", error);
    return { ok: false, error: null };
  }
};

/**
 * Send the tenant's identity document.
 *
 * Awaited: a tenant who believes they have filed their ID and has not is
 * exactly what the step exists to prevent.
 */
export const submitIdentity = async (
  id: string,
  file: File
): Promise<{ file_name: string } | null> => {
  try {
    const encoded = await toDocumentBase64(file);
    const response = await axios.post(`${base_url}/onboarding/submit_identity`, {
      id,
      file: encoded,
      file_name: file.name,
    });
    if (response.data?.error !== false) return null;
    return response.data.identity;
  } catch (error) {
    console.error("Failed to submit the identity document:", error);
    return null;
  }
};

/**
 * Send the tenant's liability insurance certificate.
 *
 * Awaited, like the other submissions: a tenant who believes they have filed
 * their certificate and has not is precisely what the step exists to prevent.
 */
export const submitInsurance = async (
  id: string,
  file: File
): Promise<{ file_url: string; file_name: string } | null> => {
  try {
    const encoded = await toDocumentBase64(file);
    const response = await axios.post(`${base_url}/onboarding/submit_insurance`, {
      id,
      file: encoded,
      file_name: file.name,
    });
    if (response.data?.error !== false) return null;
    return response.data.insurance;
  } catch (error) {
    console.error("Failed to submit the insurance certificate:", error);
    return null;
  }
};

export interface OnboardingState {
  onboarding: Onboarding | null;
  /** True only while a fetch is actually in flight. */
  loading: boolean;
  /** Set when the id was present but the journey could not be loaded. */
  failed: boolean;
  /** Records a step with the DMS and updates the local copy. */
  markStepComplete: (step: StepId) => void;
}

/**
 * Load the journey for the id in the URL.
 *
 * With no id — a direct visit to /tenant, or a design review — this resolves to
 * no journey and the caller keeps its own defaults, so the screens stay
 * reviewable without a live reservation behind them.
 */
export const useOnboarding = (id: string | null): OnboardingState => {
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [loading, setLoading] = useState<boolean>(!!id);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) {
      setOnboarding(null);
      setLoading(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    fetchOnboarding(id)
      .then((next) => {
        if (cancelled) return;
        setOnboarding(next);
        setFailed(next === null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to fetch onboarding:", error);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const markStepComplete = useCallback(
    (step: StepId) => {
      if (!id) return;
      // Optimistic: the tenant has done the work, so the rail should show it
      // ticked immediately rather than after a round trip.
      setOnboarding((current) => (current
        ? {
          ...current,
          steps: {
            ...current.steps,
            [step]: { state: "completed", completed_at: new Date().toISOString() },
          },
        }
        : current));
      completeStep(id, step);
    },
    [id]
  );

  return { onboarding, loading, failed, markStepComplete };
};
